import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useFavorites } from '@/components/favorites/FavoritesProvider';
import { isActivityPubliclyVisible } from '@/lib/events/publicVisibility';

const DAY_MS = 24 * 60 * 60 * 1000;

interface SavedFeedRow {
  id: string;
  title: string;
  start_date: string | null;
  end_date: string | null;
  timing_mode: string | null;
  outing_tags: string[] | null;
  custom_lat: number | null;
  custom_lng: number | null;
  created_at: string | null;
}

export interface SavedFeedItem {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  timingMode: string;
  outingTags: string[];
  lat: number | null;
  lng: number | null;
  isSoon: boolean;
  isNew: boolean;
}

async function fetchEventById(eventId: string): Promise<SavedFeedRow | null> {
  const { data, error } = await supabase.rpc('get_public_events_with_bookings', {
    p_event_id: eventId,
    p_limit: 1,
  });

  if (error) return null;
  const row = (Array.isArray(data) ? data[0] : data) as SavedFeedRow | undefined;
  return row ?? null;
}

async function fetchEventsByProducerIds(producerIds: string[]): Promise<SavedFeedRow[]> {
  if (producerIds.length === 0) return [];

  const { data, error } = await supabase.rpc('get_public_events_with_bookings', {
    p_producer_ids: producerIds,
    p_limit: 120,
  });

  if (error) return [];
  return ((data as SavedFeedRow[] | null) ?? []);
}

export function useSavedFeed() {
  const { listFavorites, isReady } = useFavorites();

  const activityIds = useMemo(
    () => Array.from(new Set(listFavorites('activity').map((item) => item.entityId))),
    [listFavorites]
  );
  const producerIds = useMemo(
    () => Array.from(new Set(listFavorites('producer').map((item) => item.entityId))),
    [listFavorites]
  );

  return useQuery<SavedFeedItem[]>({
    queryKey: ['saved-feed', activityIds, producerIds],
    enabled: isReady,
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (activityIds.length === 0 && producerIds.length === 0) return [];

      const [activityRows, producerRows] = await Promise.all([
        Promise.all(activityIds.map((id) => fetchEventById(id))),
        fetchEventsByProducerIds(producerIds),
      ]);

      const deduped = new Map<string, SavedFeedRow>();
      for (const row of activityRows) {
        if (row?.id) deduped.set(row.id, row);
      }
      for (const row of producerRows) {
        if (row?.id && !deduped.has(row.id)) deduped.set(row.id, row);
      }

      const rows = Array.from(deduped.values());
      if (rows.length === 0) return [];

      const now = new Date();

      const visibleUpcoming = rows.filter((row) => {
        if (!row.start_date) return false;
        const timingMode = row.timing_mode ?? 'date_time';
        const isVisible = isActivityPubliclyVisible(
          {
            timingMode,
            startDate: row.start_date,
            endDate: row.end_date,
          },
          now
        );
        if (!isVisible) return false;
        const startTs = new Date(row.start_date).getTime();
        return Number.isFinite(startTs) && startTs >= now.getTime();
      });

      if (visibleUpcoming.length === 0) return [];

      const items = visibleUpcoming
        .map((row) => {
          const startDate = row.start_date;
          const endDate = row.end_date ?? row.start_date;
          if (!startDate || !endDate) return null;

          const daysUntilStart = (new Date(startDate).getTime() - now.getTime()) / DAY_MS;
          const isSoon = daysUntilStart >= 0 && daysUntilStart <= 3;

          const createdTs = row.created_at ? new Date(row.created_at).getTime() : Number.NaN;
          const isNew = Number.isFinite(createdTs) && (now.getTime() - createdTs) / DAY_MS <= 7;

          return {
            id: row.id,
            title: row.title,
            startDate,
            endDate,
            timingMode: row.timing_mode ?? 'date_time',
            outingTags: row.outing_tags ?? [],
            lat: row.custom_lat,
            lng: row.custom_lng,
            isSoon,
            isNew,
          } as SavedFeedItem;
        })
        .filter((item): item is SavedFeedItem => item !== null);

      return items
        .sort((left, right) => {
          if (left.isSoon !== right.isSoon) return left.isSoon ? -1 : 1;
          if (left.isNew !== right.isNew) return left.isNew ? -1 : 1;
          return new Date(left.startDate).getTime() - new Date(right.startDate).getTime();
        })
        .slice(0, 3);
    },
  });
}